import { useRef, useEffect } from 'react';
import { isChatInProgress } from './chatState';

const API_URL = (process.env.REACT_APP_API_URL ?? '').trim().replace(/\/+$/, '');

// Derive allowed origins dynamically from environment or current location
const getAllowedOrigins = () => {
  const origins = new Set();
  try {
    if (API_URL) origins.add(new URL(API_URL).origin);
  } catch (e) {}
  try {
    const envBack = (process.env.REACT_APP_BACKEND_URL ?? '').trim();
    if (envBack) origins.add(new URL(envBack).origin);
  } catch (e) {}
  const extraOrigins = (process.env.REACT_APP_ALLOWED_ORIGINS ?? '')
    .split(',')
    .map(value => value.trim())
    .filter(Boolean);
  extraOrigins.forEach(origin => {
    try {
      origins.add(new URL(origin).origin);
    } catch (_) {}
  });
  // include current origin for same-origin setups
  origins.add(window.location.origin);
  return Array.from(origins);
};

// Completely isolated component that only handles iframe reloads
// This component never updates its state, so it won't cause parent re-renders
export default function BoardIframe({ iframeRef, setIsBoardLoading, selectedTemplate = 'template-1', getAuthHeaders }) {
  const lastModifiedRef = useRef(null);
  const isInitialLoadRef = useRef(true);
  const lastTemplateRef = useRef(selectedTemplate);
  
  // Log when component mounts
  useEffect(() => {
    console.log('🎬 BoardIframe component mounted!');
    console.log('🎬 iframeRef.current:', iframeRef.current);
    console.log('🎬 API_URL:', API_URL);
    return () => {
      console.log('🎬 BoardIframe component unmounting...');
    };
  }, []);

  // Reset lastModified when template changes
  useEffect(() => {
    if (lastTemplateRef.current !== selectedTemplate) {
      console.log(`Template changed from ${lastTemplateRef.current} to ${selectedTemplate}, resetting lastModified`);
      lastModifiedRef.current = null;
      lastTemplateRef.current = selectedTemplate;
      isInitialLoadRef.current = true;
    }
  }, [selectedTemplate]);

  // Poll for board.html changes and reload iframe when updated
  useEffect(() => {
    let pollCount = 0;
    const checkForUpdates = async () => {
      pollCount++;
      console.log(`[Poll #${pollCount}] Checking for board updates (template: ${selectedTemplate})...`);
      try {
        // Skip polling if offline
        if (typeof navigator !== 'undefined' && navigator && navigator.onLine === false) {
          console.warn('[BoardIframe] Offline, skipping poll');
          return;
        }

        const authHeaders = getAuthHeaders ? await getAuthHeaders() : {};
        const response = await fetch(`${API_URL}/board/last-modified?template=${encodeURIComponent(selectedTemplate)}`, { headers: authHeaders });
        const data = await response.json();
        console.log(`[Poll #${pollCount}] Response:`, data);
        
        if (data.exists && data.last_modified) {
          if (lastModifiedRef.current === null) {
            // First load - just set the timestamp in ref
            lastModifiedRef.current = data.last_modified;
            console.log(`[Poll #${pollCount}] Initial board timestamp set:`, data.last_modified);
          } else if (data.last_modified !== lastModifiedRef.current) {
            // File was modified (checking != instead of > to catch any changes)
            console.log(`[Poll #${pollCount}] ⚠️ Board file changed! Old:`, lastModifiedRef.current, 'New:', data.last_modified);
            // File was modified - but check if chat is in progress first
            const chatInProgress = isChatInProgress();
            console.log(`[Poll #${pollCount}] Chat in progress:`, chatInProgress);
            
            if (chatInProgress) {
              // Chat request is in progress, delay reload to avoid interrupting AI response
              // Will retry on next poll (in 3 seconds)
              console.log(`[Poll #${pollCount}] ⏸️ Chat in progress, delaying iframe reload until chat completes...`);
              return;
            }
            
            // File was modified and chat is not in progress - reload iframe only
            // Store the timestamp but delay the actual reload
            const modifiedTime = data.last_modified;
            lastModifiedRef.current = modifiedTime;
            
            if (iframeRef.current) {
              console.log(`[Poll #${pollCount}] ✅ Board updated, scheduling reload in 2 seconds...`);
              // Show loading indicator when reload starts
              if (setIsBoardLoading) {
                setIsBoardLoading(true);
              }
              
              // Add a delay to ensure any pending chat responses are fully processed
              setTimeout(() => {
                // Double-check chat is still not in progress before reloading
                if (iframeRef.current && !isChatInProgress()) {
                  // For cross-origin iframes, we must use cache-busting instead of reload()
                  // because contentWindow.location.reload() is blocked by CORS
                  const baseSrc = `${API_URL}/board.html?template=${encodeURIComponent(selectedTemplate)}`;
                  // Use both modifiedTime and current timestamp for better cache-busting
                  const cacheBuster = `${modifiedTime}_${Date.now()}`;
                  const newSrc = `${baseSrc}&_t=${cacheBuster}`;
                  
                  console.log(`[Poll #${pollCount}] 🔄 Reloading iframe with cache-busting:`, newSrc);
                  console.log(`[Poll #${pollCount}] Current iframe src:`, iframeRef.current.src);
                  
                  // Always update src to force reload, even if it looks similar
                  iframeRef.current.src = newSrc;
                  
                  // Double-check it was set
                  setTimeout(() => {
                    console.log(`[Poll #${pollCount}] ✅ Iframe src after update:`, iframeRef.current?.src);
                  }, 100);
                } else {
                  console.log(`[Poll #${pollCount}] ⏸️ Reload skipped - chat still in progress`);
                  // Hide loading if we skipped reload
                  if (setIsBoardLoading) {
                    setIsBoardLoading(false);
                  }
                }
              }, 2000); // 2 second delay to ensure chat response is fully processed and rendered
            } else {
              console.log(`[Poll #${pollCount}] ⚠️ iframeRef.current is null!`);
            }
          } else {
            console.log(`[Poll #${pollCount}] No changes detected (timestamp: ${data.last_modified})`);
          }
        } else {
          console.log(`[Poll #${pollCount}] ⚠️ Board file doesn't exist or no timestamp`);
        }
      } catch (error) {
        console.error(`[Poll #${pollCount}] ❌ Error checking for board updates:`, error);
        // Notify parent about polling errors
        if (setIsBoardLoading) {
          // Don't set loading to false on polling errors, just log them
          // The iframe itself might still be working
        }
      }
    };

    // Check immediately, then every 4 seconds (slightly longer to reduce load)
    console.log('🚀 Starting board polling...');
    checkForUpdates();
    const interval = setInterval(checkForUpdates, 4000);
    console.log('✅ Polling interval set to 4 seconds');

    return () => {
      console.log('🛑 Stopping board polling...');
      clearInterval(interval);
    };
  }, [iframeRef, selectedTemplate]);

  // Handle initial load for restoring state using postMessage (cross-origin safe)
  useEffect(() => {
    const handleMessage = (event) => {
      // Accept messages from backend origin
      const allowed = getAllowedOrigins();
      const isBackendOrigin = allowed.some(o => event.origin === o || event.origin.startsWith(o));
      
      if (!isBackendOrigin) {
        return;
      }
      
      if (event.data.type === 'IFRAME_LOADED' && iframeRef.current) {
        // Hide loading indicator when iframe is loaded
        if (setIsBoardLoading) {
          setIsBoardLoading(false);
        }
        
        // Restore state on initial load or after reload
        if (isInitialLoadRef.current) {
          const raw = localStorage.getItem('daily-huddle-board');
          if (raw) {
            try {
              const data = JSON.parse(raw);
              // Send restore message to iframe via postMessage
              // Use a safe target origin when posting — prefer API_URL origin or allow any if not available
              let targetOrigin = '*';
              try {
                if (API_URL) targetOrigin = new URL(API_URL).origin;
              } catch (e) {}
              iframeRef.current.contentWindow.postMessage({
                type: 'RESTORE_STATE',
                payload: data
              }, targetOrigin);
              console.log('State restored to iframe');
            } catch (err) {
              console.error('Restore failed', err);
            }
          }
          isInitialLoadRef.current = false;
        } else {
          // Iframe reloaded after update - just log it
          console.log('Iframe reloaded successfully');
        }
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [iframeRef]);

  return null; // This component doesn't render anything, it just manages the iframe
}
